"use client";

// ============================================================
// CLUBHUB – Das Blatt einer Gruppe: Kopf, Kennzahlen, Register
// ============================================================
// Steht im Layout von /gruppen/[id] und bleibt beim Wechsel der
// Register stehen. Die Kennzahlen rechnen auf dem Bestand im Browser
// — wer jemanden aufnimmt, sieht die Zahl im Kopf sofort mitgehen.
//
// Die Leiste unter dem Kopf beantwortet, was ein Vorstand oder eine
// Trainerin im Vorbeigehen wissen will: wie viele, wer hat ein Amt,
// was hat sich im letzten Jahr getan — und wer NICHT auf das
// Mannschaftsfoto darf.
// ============================================================
import { useState } from "react";
import Link from "next/link";
import { ArchiveRestore, Download, Mail, UserPlus, UsersRound } from "lucide-react";
import {
  Button,
  EmptyState,
  PageHeader,
  RegisterLeiste,
  StatStrip,
  StatStripAside,
  StatStripValue,
  buttonVariants,
  toast,
} from "@/components/ui";
import { Listenseite } from "@/components/liste";
import { TonBadge } from "@/components/mitglieder/status-badge";
import { GROUP_KIND } from "@/lib/labels";
import { aktuelleZeitraeume, alterAm, aktuellePersonen, bewegungen, kinderVon, mitgliederzahl, stand, vorfahren } from "@/lib/gruppen/logik";
import { plusTage } from "@/lib/gruppen/datum";
import { cn, formatDate } from "@/lib/utils";
import { useGruppenBestand } from "./gruppen-bestand";
import { GruppenMenue, useGruppeArchiv } from "./gruppen-menue";
import { GruppeDialog } from "./gruppe-dialog";
import { MitgliederDialog } from "./mitglieder-dialog";
import { GruppenMarke } from "./zeichen";

export function GruppeBlatt({ id, children }: { id: string; children: React.ReactNode }) {
  const b = useGruppenBestand();
  const { wiederherstellen } = useGruppeArchiv();
  const [dialog, setDialog] = useState<null | "bearbeiten" | "unter" | "mitglieder">(null);
  const gruppe = b.gruppen.find((g) => g.id === id);

  if (!gruppe) {
    // Im Musterbestand: eine eben angelegte Gruppe nach dem Neuladen.
    return (
      <EmptyState
        icon={UsersRound}
        title="Diese Gruppe gibt es nicht mehr"
        description={
          b.quelle === "muster"
            ? "Im Musterbestand gelten neu angelegte Gruppen nur bis zum Neuladen der Seite."
            : "Vielleicht wurde sie eben an anderer Stelle geändert. Archivierte Gruppen findest du in der Liste unter „Archiv“."
        }
        action={
          <Link href="/gruppen" className={buttonVariants({ variant: "outline" })}>
            Zu den Gruppen
          </Link>
        }
      />
    );
  }

  const { heute, zugehoerigkeiten, personen } = b;
  const eigene = zugehoerigkeiten.filter((z) => z.groupId === id);
  const zahl = mitgliederzahl(b.gruppen, zugehoerigkeiten, id, heute);
  const aktuell = eigene.filter((z) => stand(z, heute) === "aktuell");
  const mitFunktion = new Set(aktuell.filter((z) => z.function).map((z) => z.contactId)).size;
  const { zugaenge, abgaenge } = bewegungen(eigene, plusTage(heute, -365), heute);
  // Wie der Knopf „Aktuell“ im Register: Laufende und kuenftige Zugaenge, je Person einmal.
  const imRegister = aktuelleZeitraeume(eigene, heute);
  const registerZahl = new Set(imRegister.map((z) => z.contactId)).size;
  const kommend = imRegister.filter((z) => stand(z, heute) === "kuenftig");
  const leute = [...aktuellePersonen(zugehoerigkeiten, [id], heute)].map((cid) => personen[cid]).filter((p) => p && !p.isOrganization);
  const alter = leute.map((p) => alterAm(p!.birthDate, heute)).filter((a): a is number => a !== null);
  const schnitt = alter.length >= 3 ? Math.round(alter.reduce((s, a) => s + a, 0) / alter.length) : null;
  const ohneFoto = leute.filter((p) => !p!.consentPhotos).length;
  const weg = vorfahren(b.gruppen, id);
  const untergruppen = kinderVon(b.gruppen, id).filter((g) => !g.isArchived).length;

  const darfAufnehmen = b.rechte.schreiben && !gruppe.isArchived;

  function exportieren() {
    // BACKEND: GET /api/gruppen/<id>/export.csv — Name, Nr., Funktion, seit, bis; nur mit `members.read`, Abruf ins AuditLog
    toast.success("Export wird vorbereitet", {
      description: `CSV mit ${aktuell.length} aktuellen Zugehörigkeiten (Name, Nr., Funktion, seit, bis). Kommt mit dem Backend; jeder Abruf wird protokolliert.`,
    });
  }

  return (
    <Listenseite>
      <PageHeader
        back={{ href: "/gruppen", label: "Gruppen" }}
        leading={<GruppenMarke kind={gruppe.kind} farbe={gruppe.color} />}
        title={gruppe.name}
        badge={gruppe.isArchived ? <TonBadge ton="neutral">Archiviert</TonBadge> : undefined}
        meta={weg.length > 0 ? `in ${weg.map((w) => w.name).join(" › ")}` : undefined}
        description={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span>{GROUP_KIND[gruppe.kind]}</span>
            {gruppe.sportCode && <span className="text-text-tertiary">· {gruppe.sportCode}</span>}
            {gruppe.emailAlias && (
              <>
                <span className="text-text-tertiary" aria-hidden>
                  ·
                </span>
                <a href={`mailto:${gruppe.emailAlias}`} className="hover:text-accent truncate">
                  {gruppe.emailAlias}
                </a>
              </>
            )}
          </span>
        }
        actions={
          <>
            {darfAufnehmen && (
              <Button onClick={() => setDialog("mitglieder")}>
                <UserPlus className="h-4 w-4" aria-hidden />
                Mitglieder hinzufügen
              </Button>
            )}
            {gruppe.isArchived && b.rechte.schreiben && (
              <Button variant="outline" onClick={() => void wiederherstellen(gruppe)}>
                <ArchiveRestore className="h-4 w-4" aria-hidden />
                Wiederherstellen
              </Button>
            )}
            {!gruppe.isArchived && (
              <Link href={`/nachrichten/neu?gruppe=${gruppe.id}`} className={buttonVariants({ variant: "secondary" })}>
                <Mail className="h-4 w-4" aria-hidden />
                Nachricht an die Gruppe
              </Link>
            )}
            <Button variant="ghost" onClick={exportieren} title="Als CSV exportieren">
              <Download className="h-4 w-4" aria-hidden />
              <span className="max-md:hidden">Exportieren</span>
              <span className="md:hidden">CSV</span>
            </Button>
            <GruppenMenue gruppe={gruppe} onBearbeiten={() => setDialog("bearbeiten")} onUntergruppe={() => setDialog("unter")} className="h-9 w-9" />
          </>
        }
      />

      {gruppe.isArchived && (
        <p className="bg-bg-tertiary text-body-sm text-text-secondary rounded-lg px-4 py-2.5">
          Archiviert. Die Gruppe nimmt niemanden mehr auf und steht in keinem Verteiler; ihre Mitglieder und die ganze Historie bleiben lesbar.
        </p>
      )}

      {gruppe.description && <p className="text-body-sm text-text-secondary -mt-1 max-w-3xl md:hidden">{gruppe.description}</p>}

      <StatStrip>
        <StatStripValue
          titel="Mitglieder"
          wert={String(zahl.gesamt)}
          zusatz={
            [
              zahl.gesamt !== zahl.direkt ? `${zahl.direkt} direkt` : null,
              kommend.length > 0 ? `${kommend.length} ab ${formatDate(kommend.map((z) => z.since ?? "").sort()[0])}` : null,
            ]
              .filter(Boolean)
              .map((t) => `· ${t}`)
              .join(" ") || undefined
          }
          betont
        />
        <StatStripValue titel="Mit Funktion" wert={String(mitFunktion)} />
        <StatStripValue titel="Zugänge · 12 Monate" wert={zugaenge > 0 ? `+${zugaenge}` : "0"} />
        <StatStripValue titel="Abgänge · 12 Monate" wert={abgaenge > 0 ? `−${abgaenge}` : "0"} />
        {schnitt !== null && (
          <StatStripValue titel="Alter" wert={`Ø ${schnitt}`} zusatz={`· ${Math.min(...alter)} bis ${Math.max(...alter)}`} />
        )}
        <StatStripAside>
          {untergruppen > 0 && <StatStripValue titel="Untergruppen" wert={String(untergruppen)} />}
          {leute.length > 0 && (
            <Link
              href={`/gruppen/${id}?foto=ohne`}
              title="Wer nicht auf Mannschaftsfotos, in Spielberichten und auf der Website erscheinen darf"
              className={cn("-m-1.5 rounded-lg p-1.5 transition-colors", "hover:bg-bg-secondary duration-fast")}
            >
              <StatStripValue titel="Ohne Foto-Einwilligung" wert={String(ohneFoto)} ton={ohneFoto > 0 ? "warnung" : "neutral"} />
            </Link>
          )}
        </StatStripAside>
      </StatStrip>

      <RegisterLeiste
        basis={`/gruppen/${id}`}
        register={[
          { schluessel: "", label: "Mitglieder", zaehler: registerZahl },
          { schluessel: "untergruppen", label: "Untergruppen", zaehler: untergruppen },
          { schluessel: "termine", label: "Termine" },
        ]}
      />

      {children}

      <GruppeDialog offen={dialog === "bearbeiten"} onClose={() => setDialog(null)} gruppe={gruppe} />
      <GruppeDialog offen={dialog === "unter"} onClose={() => setDialog(null)} vorgabeEltern={gruppe.id} />
      <MitgliederDialog offen={dialog === "mitglieder"} onClose={() => setDialog(null)} gruppe={gruppe} />
    </Listenseite>
  );
}
